/**
 * BusinessUnitForm Component
 *
 * Reusable form for creating and editing business units
 */

import {
  EntityAutocompleteField,
  TextField,
  SelectField,
  type CreateBusinessUnitFormData,
} from '../forms';
import { createBusinessUnitSchema } from '../forms/validation';
import { EntityForm } from '../shared';
import { TagPicker } from '../tags';
import { useBusinessUnits } from '../../graphql/queries';
import '@tylertech/forge/card';
import '@tylertech/forge/icon';
import './BusinessUnitForm.css';

export interface BusinessUnitFormProps {
  /** Initial form values (for edit mode) */
  defaultValues?: Partial<CreateBusinessUnitFormData>;
  /**
   * The entityId of the business unit being edited. Excluded from the
   * Parent Business Unit picker's option list so a unit can never be set as
   * its own parent (issue #772, mirrors ServiceQualityForm's
   * `excludeCategoryId`, issue #774).
   */
  excludeBusinessUnitId?: string;
  /** Submit handler */
  onSubmit: (data: CreateBusinessUnitFormData) => void | Promise<void>;
  /** Cancel handler */
  onCancel: () => void;
  /** Is form submitting */
  isSubmitting?: boolean;
  /** Form mode */
  mode: 'create' | 'edit';
}

const CardElement = 'forge-card' as React.ElementType;

/**
 * BusinessUnit create/edit form with validation
 *
 * @example
 * ```tsx
 * <BusinessUnitForm
 *   mode="create"
 *   onSubmit={handleCreate}
 *   onCancel={() => navigate('/business-units')}
 * />
 * ```
 */
export function BusinessUnitForm({
  defaultValues,
  excludeBusinessUnitId,
  onSubmit,
  onCancel,
  isSubmitting = false,
  mode,
}: BusinessUnitFormProps): JSX.Element {
  // Full catalog (all pages, not one capped server page) so the Parent
  // Business Unit picker can search/select any existing unit (issue #772).
  const { data: businessUnitsData } = useBusinessUnits();
  const parentBusinessUnitOptions = (businessUnitsData?.businessUnits?.edges ?? [])
    .map((edge) => edge.node)
    .filter((unit) => unit.entityId !== excludeBusinessUnitId)
    .map((unit) => ({ value: unit.entityId, label: unit.name }));

  return (
    <EntityForm<CreateBusinessUnitFormData>
      schema={createBusinessUnitSchema}
      baseValues={{
        unitType: 'division',
        lifecycleStatus: 'active',
        tags: [],
      }}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      onCancel={onCancel}
      isSubmitting={isSubmitting}
      mode={mode}
      createLabel="Create Business Unit"
      formClassName="business-unit-form"
    >
      {({ register, formState: { errors }, values, setValue }) => {
        const tags = values.tags || [];

        return (
          <>
            <CardElement className="form-card">
              <div className="form-section">
                <h2 className="forge-typography--headline6">Basic Information</h2>

                <TextField
                  label="Name"
                  {...register('name')}
                  value={values.name || ''}
                  error={errors.name}
                  required
                  placeholder="e.g., Engineering Division"
                  helperText="A descriptive name for the business unit"
                />

                <TextField
                  label="Description"
                  {...register('description')}
                  value={values.description || ''}
                  error={errors.description}
                  placeholder="Brief description of the business unit"
                  helperText="Optional description (max 2000 characters)"
                />
              </div>
            </CardElement>

            <CardElement className="form-card">
              <div className="form-section">
                <h2 className="forge-typography--headline6">Classification</h2>

                <SelectField
                  label="Unit Type"
                  {...register('unitType')}
                  value={values.unitType}
                  options={[
                    { value: 'division', label: 'Division' },
                    { value: 'group', label: 'Group' },
                    { value: 'team', label: 'Team' },
                    { value: 'partner', label: 'Partner' },
                  ]}
                  error={errors.unitType}
                  required
                />

                <SelectField
                  label="Lifecycle Status"
                  {...register('lifecycleStatus')}
                  value={values.lifecycleStatus || ''}
                  options={[
                    { value: 'active', label: 'Active' },
                    { value: 'inactive', label: 'Inactive' },
                    { value: 'merged', label: 'Merged' },
                    { value: 'disbanded', label: 'Disbanded' },
                  ]}
                  error={errors.lifecycleStatus}
                />
              </div>
            </CardElement>

            <CardElement className="form-card">
              <div className="form-section">
                <h2 className="forge-typography--headline6">Hierarchy</h2>

                <EntityAutocompleteField
                  name="parentBusinessUnitId"
                  label="Parent Business Unit"
                  options={parentBusinessUnitOptions}
                  value={values.parentBusinessUnitId || ''}
                  onChange={(unitId) =>
                    setValue('parentBusinessUnitId', unitId, { shouldValidate: true })
                  }
                  error={errors.parentBusinessUnitId}
                  placeholder="Search business units…"
                  helperText="Optional parent business unit for hierarchy"
                  emptyMessage="No business units match your search"
                />

                <TextField
                  label="Code"
                  {...register('code')}
                  value={values.code || ''}
                  error={errors.code}
                  placeholder="e.g., ENG"
                  helperText="Optional short code, max 100 chars"
                />
              </div>
            </CardElement>

            <CardElement className="form-card">
              <div className="form-section">
                <h2 className="forge-typography--headline6">Additional Information</h2>

                <TagPicker
                  id="business-unit-tag-picker"
                  label="Tags"
                  selectedTagIds={tags}
                  onChange={(newIds) => setValue('tags', newIds)}
                />
              </div>
            </CardElement>
          </>
        );
      }}
    </EntityForm>
  );
}
